/**
 * Bulk Bear result entry (spec sections 24, 72).
 *
 * One operator types a whole rally while reading the in-game result screen, so
 * this is a grid, not a wizard: dense rows, Tab moves across a row, Enter on
 * the last row adds another, and a tab-separated paste from a spreadsheet fills
 * the grid in one action.
 *
 * The rule that shapes everything else here: **blank stays blank**. Troop
 * counts are frequently not visible to the person recording, and a blank cell
 * is submitted as absent, never as 0. Writing 0 would silently corrupt every
 * damage-per-troop figure computed from the session afterwards, and unlike a
 * missing value it would look like a measurement.
 *
 * The legend under the grid says that out loud in whichever language the
 * operator reads, because it is the one instruction that stops a well-meaning
 * person from typing zeroes into cells they could not see.
 */

import { useMemo, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import type { CreatedBearRally, PlayerSummary } from '@kso/schemas';
import { Card, Notice, WarningList } from '../../components/ui/primitives';
import { ErrorState } from '../../components/ui/states';
import { useRecordBearRally } from '../../api/queries';
import { parseOptionalInt } from '../../lib/format';
import { useT } from '../../i18n';
import type { Dictionary } from '../../i18n';
import { buildRallyPayload } from './buildRallyPayload';

type MemberRow = {
  key: string;
  playerId: string;
  joinOrder: string;
  captainHeroId: string;
  infantry: string;
  cavalry: string;
  archer: string;
  totalTroops: string;
  damage: string;
};

const MEMBER_FIELDS = [
  'playerId',
  'joinOrder',
  'captainHeroId',
  'infantry',
  'cavalry',
  'archer',
  'totalTroops',
  'damage',
] as const;

type MemberField = (typeof MEMBER_FIELDS)[number];

/** The cell fields, in the order they appear left to right after the joiner. */
const NUMERIC_FIELDS = [
  'joinOrder',
  'captainHeroId',
  'infantry',
  'cavalry',
  'archer',
  'totalTroops',
  'damage',
] as const;

/** Column header per numeric field, for the aria-label on each cell. */
function fieldLabel(t: Dictionary, field: (typeof NUMERIC_FIELDS)[number]): string {
  switch (field) {
    case 'joinOrder':
      return t.rallyEntry.columns.joinOrder;
    case 'captainHeroId':
      return t.rallyEntry.columns.captainHero;
    case 'infantry':
      return t.rallyEntry.columns.infantry;
    case 'cavalry':
      return t.rallyEntry.columns.cavalry;
    case 'archer':
      return t.rallyEntry.columns.archer;
    case 'totalTroops':
      return t.rallyEntry.columns.totalTroops;
    case 'damage':
      return t.rallyEntry.columns.damage;
    default:
      return field;
  }
}

function blankRow(): MemberRow {
  return {
    key: crypto.randomUUID(),
    playerId: '',
    joinOrder: '',
    captainHeroId: '',
    infantry: '',
    cavalry: '',
    archer: '',
    totalTroops: '',
    damage: '',
  };
}

export function RallyEntryForm({
  sessionId,
  members,
  onRecorded,
}: {
  sessionId: string;
  members: PlayerSummary[];
  onRecorded?: () => void;
}) {
  const t = useT();
  const record = useRecordBearRally(sessionId);

  const [leaderPlayerId, setLeaderPlayerId] = useState('');
  const [wave, setWave] = useState('');
  const [sequence, setSequence] = useState('');
  const [rallyCapacity, setRallyCapacity] = useState('');
  const [actualTroops, setActualTroops] = useState('');
  const [totalDamage, setTotalDamage] = useState('');
  const [leaderHeroIds, setLeaderHeroIds] = useState('');
  const [rows, setRows] = useState<MemberRow[]>(() =>
    Array.from({ length: 8 }, () => blankRow()),
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const sortedMembers = useMemo(
    () =>
      [...members].sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id, undefined, {
        sensitivity: 'base',
      })),
    [members],
  );

  const filledRows = rows.filter((row) => row.playerId.trim() !== '');

  function updateRow(key: string, field: MemberField, value: string) {
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
    );
  }

  function onRowKeyDown(event: KeyboardEvent<HTMLTableSectionElement>) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    setRows((current) => [...current, blankRow()]);
  }

  /**
   * Tab-separated paste, so a spreadsheet the alliance already keeps can be
   * dropped straight in. Columns map left to right from the cell pasted into;
   * anything past the last column is ignored rather than silently reinterpreted.
   */
  function onPaste(event: ClipboardEvent<HTMLInputElement>, rowIndex: number, field: MemberField) {
    const text = event.clipboardData.getData('text/plain');
    if (!text.includes('\t') && !text.includes('\n')) return;
    event.preventDefault();

    const lines = text.replace(/\r/g, '').split('\n').filter((line) => line.trim() !== '');
    const startColumn = MEMBER_FIELDS.indexOf(field);

    setRows((current) => {
      const next = [...current];
      lines.forEach((line, lineIndex) => {
        const targetIndex = rowIndex + lineIndex;
        while (next.length <= targetIndex) next.push(blankRow());
        const existing = next[targetIndex];
        if (!existing) return;
        const updated = { ...existing };
        line.split('\t').forEach((cell, cellIndex) => {
          const column = MEMBER_FIELDS[startColumn + cellIndex];
          if (!column) return;
          updated[column] = cell.trim();
        });
        next[targetIndex] = updated;
      });
      return next;
    });
  }

  function submit() {
    setSubmitError(null);

    const result = buildRallyPayload(
      { leaderPlayerId, wave, sequence, rallyCapacity, actualTroops, totalDamage, leaderHeroIds },
      rows,
    );

    if (!result.ok) {
      setSubmitError(result.message);
      return;
    }

    record.mutate(result.payload, {
      onSuccess: () => {
        setLeaderPlayerId('');
        setWave('');
        setSequence('');
        setRallyCapacity('');
        setActualTroops('');
        setTotalDamage('');
        setLeaderHeroIds('');
        setRows(Array.from({ length: 8 }, () => blankRow()));
        onRecorded?.();
      },
    });
  }

  return (
    <Card
      title={t.rallyEntry.title}
      actions={
        <span className="muted small">{t.rallyEntry.joinersEntered(filledRows.length)}</span>
      }
    >
      <div className="row" style={{ gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
        <label className="row" style={{ gap: 6 }}>
          <span className="muted small">{t.rallyEntry.rallyLead}</span>
          <select
            value={leaderPlayerId}
            onChange={(event) => setLeaderPlayerId(event.target.value)}
            style={{ minWidth: 180 }}
          >
            <option value="">{t.common.selectPlaceholder}</option>
            {sortedMembers.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name ?? member.id}
              </option>
            ))}
          </select>
        </label>

        <NumberField label={t.rallyEntry.wave} value={wave} onChange={setWave} width={64} />
        <NumberField label={t.rallyEntry.order} value={sequence} onChange={setSequence} width={64} />
        <NumberField
          label={t.rallyEntry.rallyCapacity}
          value={rallyCapacity}
          onChange={setRallyCapacity}
          width={100}
        />
        <NumberField
          label={t.rallyEntry.actualTroops}
          value={actualTroops}
          onChange={setActualTroops}
          width={100}
        />
        <NumberField
          label={t.rallyEntry.totalRallyDamage}
          value={totalDamage}
          onChange={setTotalDamage}
          width={130}
        />

        <label className="row" style={{ gap: 6 }}>
          <span className="muted small">{t.rallyEntry.leadHeroIds}</span>
          <input
            value={leaderHeroIds}
            onChange={(event) => setLeaderHeroIds(event.target.value)}
            placeholder={t.rallyEntry.leadHeroIdsPlaceholder}
            style={{ width: 150 }}
            className="mono"
          />
        </label>
      </div>

      <div className="entry-wrap">
        <table className="entry">
          <thead>
            <tr>
              <th style={{ width: 30 }}>{t.rallyEntry.columns.row}</th>
              <th style={{ minWidth: 180 }}>{t.rallyEntry.columns.joiner}</th>
              <th style={{ width: 78 }}>{t.rallyEntry.columns.joinOrder}</th>
              <th style={{ width: 96 }}>{t.rallyEntry.columns.captainHero}</th>
              <th style={{ width: 90 }}>{t.rallyEntry.columns.infantry}</th>
              <th style={{ width: 90 }}>{t.rallyEntry.columns.cavalry}</th>
              <th style={{ width: 90 }}>{t.rallyEntry.columns.archer}</th>
              <th style={{ width: 100 }}>{t.rallyEntry.columns.totalTroops}</th>
              <th style={{ width: 110 }}>{t.rallyEntry.columns.damage}</th>
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody onKeyDown={onRowKeyDown}>
            {rows.map((row, rowIndex) => (
              <tr key={row.key}>
                <td className="faint small">{rowIndex + 1}</td>
                <td>
                  <select
                    value={row.playerId}
                    onChange={(event) => updateRow(row.key, 'playerId', event.target.value)}
                    aria-label={t.rallyEntry.joinerAria(rowIndex + 1)}
                  >
                    <option value="">{t.common.dash}</option>
                    {sortedMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name ?? member.id}
                      </option>
                    ))}
                  </select>
                </td>
                {NUMERIC_FIELDS.map((field) => (
                  <td key={field}>
                    <input
                      className="num-input"
                      inputMode="numeric"
                      value={row[field]}
                      onChange={(event) => updateRow(row.key, field, event.target.value)}
                      onPaste={(event) => onPaste(event, rowIndex, field)}
                      aria-invalid={parseOptionalInt(row[field]) === 'INVALID'}
                      aria-label={t.rallyEntry.cellAria(fieldLabel(t, field), rowIndex + 1)}
                      placeholder=""
                    />
                  </td>
                ))}
                <td>
                  <button
                    type="button"
                    className="btn btn--sm btn--subtle"
                    onClick={() => setRows((current) => current.filter((item) => item.key !== row.key))}
                    aria-label={t.rallyEntry.removeRowAria(rowIndex + 1)}
                    title={t.rallyEntry.removeRowTitle}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="entry-legend">
        {t.rallyEntry.legendLead} <em>{t.rallyEntry.legendNotRecorded}</em>{' '}
        {t.rallyEntry.legendTail}
      </p>

      <div className="row" style={{ marginTop: 8 }}>
        <button
          type="button"
          className="btn btn--primary"
          onClick={submit}
          disabled={record.isPending || !leaderPlayerId}
        >
          {record.isPending ? t.common.saving : t.rallyEntry.saveRally}
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => setRows((current) => [...current, ...Array.from({ length: 5 }, () => blankRow())])}
        >
          {t.rallyEntry.add5Rows}
        </button>
        {record.isSuccess ? (
          <span style={{ color: 'var(--high)' }}>{t.rallyEntry.rallySaved}</span>
        ) : null}
      </div>

      {record.data ? <SaveReport rally={record.data} /> : null}

      {submitError ? (
        <div style={{ marginTop: 10 }}>
          <Notice severity="WARN">{submitError}</Notice>
        </div>
      ) : null}

      {record.error ? (
        <div style={{ marginTop: 10 }}>
          <ErrorState error={record.error} />
        </div>
      ) : null}
    </Card>
  );
}

function NumberField({
  label,
  value,
  onChange,
  width,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  width: number;
}) {
  return (
    <label className="row" style={{ gap: 6 }}>
      <span className="muted small">{label}</span>
      <input
        className="num-input"
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={parseOptionalInt(value) === 'INVALID'}
        style={{ width, textAlign: 'right' }}
      />
    </label>
  );
}

/**
 * What the server managed to bind, reported straight after a save.
 *
 * The gear snapshot bound to each row is what any later "did this upgrade
 * help?" reading rests on. If it could not be bound, or sits hours from the
 * rally, the operator should learn that now — while the result screen is still
 * on their other monitor and a re-sync is still worth requesting — rather than
 * weeks later when the analysis quietly refuses to draw a conclusion.
 */
function SaveReport({ rally }: { rally: CreatedBearRally['rally'] }) {
  const t = useT();
  const weak = rally.context.filter(
    (entry) => entry.contextQuality === 'STALE' || entry.contextQuality === 'MISSING',
  );

  return (
    <div className="stack" style={{ marginTop: 10, gap: 6 }}>
      <p className="muted small" style={{ margin: 0 }}>
        {t.rallyEntry.savedJoiners(rally.memberCount, rally.wave)}
      </p>

      <WarningList warnings={rally.warnings} />

      {weak.length > 0 ? (
        <Notice severity="WARN">
          {t.rallyEntry.weakBindings(weak.length, rally.context.length)}
        </Notice>
      ) : null}
    </div>
  );
}
